import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PublicRoute } from '../types/publicTypes';

interface NavigationContextType {
  currentPath: PublicRoute;
  navigate: (path: PublicRoute, options?: { scrollId?: string }) => void;
  previewTemplateId: string | null;
  setPreviewTemplateId: (id: string | null) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

const VALID_ROUTES: PublicRoute[] = [
  '/',
  '/temalar',
  '/sektorler',
  '/fiyatlandirma',
  '/ozellikler',
  '/nasil-calisir',
  '/sss',
  '/iletisim',
  '/giris',
  '/kayit',
  '/dashboard',
  '/admin',
];

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getInitialPath = (): PublicRoute => {
    if (typeof window === 'undefined') return '/';
    const path = window.location.pathname;
    if (path.startsWith('/admin')) {
      return path as PublicRoute;
    }
    if (path.startsWith('/dashboard') || path.startsWith('/workspace') || window.location.search.includes('oauth=')) {
      return path.startsWith('/dashboard') ? (path as PublicRoute) : '/dashboard';
    }
    if (VALID_ROUTES.includes(path as PublicRoute)) {
      return path as PublicRoute;
    }
    // Check hash fallback if hosted on subpath
    const hash = window.location.hash.replace('#', '');
    if (hash.startsWith('/admin')) {
      return hash as PublicRoute;
    }
    if (hash.startsWith('/dashboard') || hash.startsWith('/workspace')) {
      return '/dashboard';
    }
    if (VALID_ROUTES.includes(hash as PublicRoute)) {
      return hash as PublicRoute;
    }
    return '/';
  };

  const [currentPath, setCurrentPath] = useState<PublicRoute>(getInitialPath);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        setCurrentPath(path as PublicRoute);
      } else if (path.startsWith('/dashboard') || path.startsWith('/workspace') || window.location.search.includes('oauth=')) {
        setCurrentPath(path.startsWith('/dashboard') ? (path as PublicRoute) : '/dashboard');
      } else if (VALID_ROUTES.includes(path as PublicRoute)) {
        setCurrentPath(path as PublicRoute);
      } else {
        setCurrentPath('/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: PublicRoute, options?: { scrollId?: string }) => {
    if (path !== currentPath) {
      try {
        window.history.pushState({}, '', path);
      } catch {
        // Fallback for sandboxed iframes
      }
      setCurrentPath(path);
    }

    if (options?.scrollId) {
      setTimeout(() => {
        const el = document.getElementById(options.scrollId!);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <NavigationContext.Provider
      value={{
        currentPath,
        navigate,
        previewTemplateId,
        setPreviewTemplateId,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
